import isEmpty from "lodash-es/isEmpty";
import {
  BulkActionCount,
  DisplayFieldErrors,
  EfsMelcoEfsModelsFormat,
  EfsMelcoEfsModelsMappingFileAttribute,
  useGenericDrawer,
  useTableSelection,
} from "melco-shared-logic";
import {
  Alert,
  BulkActionContainerCol,
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  TableActionBarRow,
  Title,
  ValueOrFallback,
} from "melco-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFilenameRules } from "../../hooks/inputFormats/useFilenameRules";
import { EmptyWrapper } from "../shared/EmptyWrapper";
import { BulkActionRemoveFilenameRules } from "./BulkActionRemoveFilenameRules";
import { DisplayFilenameRuleValue } from "./DisplayFilenameRuleValue";
import { FilenameRuleDrawer } from "./FilenameRuleDrawer";

const indexAsRowKey = (_: unknown, idx: number | undefined) => idx!.toString();

const { spacings } = DesignTokens;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type FilenameRulesProps = {
  inputFormat: EfsMelcoEfsModelsFormat;
};

export const FilenameRules: React.FC<FilenameRulesProps> = ({
  inputFormat,
}) => {
  const { t } = useTranslation();

  const { filenameRules } = useFilenameRules();

  const filenameRulesDrawer = useGenericDrawer();
  const { openDrawer } = filenameRulesDrawer;

  const [editFilenameRuleIndex, setEditFilenameRuleIndex] = useState<
    number | undefined
  >(undefined);

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: EfsMelcoEfsModelsMappingFileAttribute) => {
      setEditFilenameRuleIndex(filenameRules?.findIndex((r) => r === row));
      openDrawer();
    });

  const columns = [
    {
      title: t("inputFormats.actions.filenameRules.list.columns.type"),
      key: "type",
      dataIndex: "type",
      render: (_: string, item: EfsMelcoEfsModelsMappingFileAttribute) => {
        return (
          <ValueOrFallback
            value={
              item.type
                ? t(`inputFormats.actions.filenameRules.types.${item.type}`)
                : undefined
            }
            fallback={t("global.fallback.empty")}
          />
        );
      },
    },
    {
      title: t("inputFormats.actions.filenameRules.list.columns.value"),
      key: "value",
      dataIndex: "value",
      render: (_: string, item: EfsMelcoEfsModelsMappingFileAttribute) => {
        return <DisplayFilenameRuleValue filenameRule={item} />;
      },
    },
  ];

  const handleAdd = useCallback(() => {
    setEditFilenameRuleIndex(undefined);
    openDrawer();
  }, [openDrawer, setEditFilenameRuleIndex]);

  const addButton = (
    <Button type="primary" icon={<PlusIcon />} onClick={handleAdd}>
      {t("inputFormats.actions.filenameRules.actions.add.button")}
    </Button>
  );

  const help = (
    <Alert
      type="info"
      message={t("inputFormats.actions.filenameRules.help")}
      withBackground
    />
  );

  return (
    <>
      <Title level={5}>
        {t("inputFormats.detail.form.filenameRules.title")}
      </Title>

      <DisplayFieldErrors name="filename_rules_list" aggregateErrors />

      {isEmpty(filenameRules) ? (
        <FullWidthSpace direction="vertical">
          {help}
          <EmptyWrapper data-cy-id="filename-rules-empty-wrapper">
            {addButton}
          </EmptyWrapper>
        </FullWidthSpace>
      ) : (
        <>
          {help}
          <Space direction="vertical">
            <TableWrapper>
              <TableActionBarRow>
                <BulkActionContainerCol>
                  <BulkActionCount selectedCount={selectedRowKeys.length} />
                  <BulkActionRemoveFilenameRules
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                  />
                </BulkActionContainerCol>
              </TableActionBarRow>
              <Table
                showSorterTooltip={false}
                sticky
                dataSource={filenameRules ?? []}
                columns={columns}
                rowKey={indexAsRowKey}
                rowSelection={rowSelection}
                onRow={onRow}
                rowClassName={() => "clickable"}
                pagination={false}
                data-cy-id="filename-rules-table"
              />
            </TableWrapper>

            <AddButtonWrapper>{addButton}</AddButtonWrapper>
          </Space>
        </>
      )}

      <FilenameRuleDrawer
        drawer={filenameRulesDrawer}
        inputFormat={inputFormat}
        editFilenameRuleIndex={editFilenameRuleIndex}
      />
    </>
  );
};
